import { defineMessages } from 'react-intl';
import { StateFamily, AnalysesErrorMessages } from '@algoan/dashboard/model';

export const messages = defineMessages({
  bankHistoryTitle: {
    id: 'widgets.bank_history.title',
    defaultMessage: 'Bank History',
    description: 'Title of the bank history widget',
  },
  bankStatements: {
    id: 'widgets.regular_cash_flows_analysis.title',
    defaultMessage: 'Bank Statements',
    description: 'Title of the regular cash flows analysis widget',
  },
  expenditureChart: {
    id: 'widgets.expenditure_chart.title',
    defaultMessage: 'Expenditures',
    description: 'Title of the expenditure chart widget',
  },
  synthesis: {
    id: 'widgets.debt_collection_synthesis.title',
    defaultMessage: 'Synthesis',
    description: 'Title of the debt collection synthesis widget',
  },
  debtConsolidationLoanSynthesis: {
    id: 'widgets.debt_consolidation_synthesis.title',
    defaultMessage: 'Synthesis',
    description: 'Title of the debt consolidation loan widget',
  },
  balances: {
    id: 'widgets.balances.title',
    defaultMessage: 'Balances',
    description: 'Title of the balances widget',
  },
  unavailableAnalysis: {
    id: 'dashboard.unavailable_analysis',
    defaultMessage: 'Unavailable analysis',
    description: 'Error when no analysis was found',
  },
  unavailableStatements: {
    id: 'dashboard.unavailable_statements',
    defaultMessage: 'Unavailable statements',
    description: 'Error when no transactions was found',
  },
  applicationNoLongerAvailable: {
    id: 'dashboard.application_no_longer_available_for_consultation',
    defaultMessage: 'This application is no longer available for consultation.',
    description: 'Message when the application is no longer available for consultation ',
  },
  personalLoanAnalysis: {
    id: 'widgets.personal_loan_analysis',
    defaultMessage: 'Personal loan analysis',
    description: 'Title of the personal loan analysis',
  },
  debtConsolidationLoan: {
    id: 'widgets.debt_consolidation_loan',
    defaultMessage: 'Debt consolidation loan',
    description: 'Title of the debt consolidation loan synthesis',
  },
  debtRecovery: {
    id: 'widgets.debt_recovery',
    defaultMessage: 'Debt recovery',
    description: 'Title of the debt recovery synthesis',
  },
  TechnicalErrorWithShortenedMessage: {
    id: 'dashboard.technical_error_with_shortened_message',
    defaultMessage: 'A technical error occurred.',
    description: 'short error message in case of technical error',
  },
  failedAggregationErrorMessage: {
    id: 'dashboard.failed_aggregation_error_message',
    defaultMessage: 'Failed aggregation',
    description: 'error message if the aggregation has failed',
  },
  failedAggregationErrorDescription: {
    id: 'dashboard.failed_aggregation_error_description',
    defaultMessage: 'The aggregation has failed.',
    description: 'error message if the aggregation has failed',
  },
  technicalErrorPDF: {
    id: 'dashboard.deprecated_technical_error_PDF',
    defaultMessage: 'An inconsistency has been detected in one or more of the documents submitted.',
    description: 'Error message in case of technical error for the PDF demo',
  },
  deprecatedTechnicalError: {
    id: 'dashboard.deprecated_technical_error',
    defaultMessage:
      'The aggregation failed due to technical reasons.\u000aPlease reach our support service for more details at <a>support@algoan.com</a>.',
    description: 'Error message in case of technical error',
  },
  [AnalysesErrorMessages.TECHNICAL_ERROR]: {
    id: 'dashboard.technical_error',
    defaultMessage:
      'The aggregation failed due to technical reasons.\u000aHere is the error returned by your bank: {error}',
    description: 'Error message in case of technical error',
  },
  [AnalysesErrorMessages.NOT_ENOUGH_TRANSACTIONS]: {
    id: 'dashboard.not_enough_transactions',
    defaultMessage: 'The aggregated accounts contain less than 20 transactions.',
    description: 'Error message when the analysis contains not enough transactions',
  },
  [AnalysesErrorMessages.CURRENCY_NOT_SUPPORTED]: {
    id: 'dashboard.currency_not_supported',
    defaultMessage: 'No checking or credit card account in Euros was aggregated.',
    description: 'Error message when the analysis contains account with an unsupported currency',
  },
  [AnalysesErrorMessages.USAGE_NOT_SUPPORTED]: {
    id: 'dashboard.usage_not_supported',
    defaultMessage: 'No personal usage account was aggregated.',
    description: 'Error message when the analysis contains account with an unsupported usage',
  },
  [AnalysesErrorMessages.NO_CHECKING_ACCOUNT]: {
    id: 'dashboard.no_checking_account',
    defaultMessage: 'No checking or credit card account was aggregated.',
    description: 'Error message when the analysis contains no checking account',
  },
  [AnalysesErrorMessages.OWNERSHIP_NOT_SUPPORTED]: {
    id: 'dashboard.ownership_not_supported',
    defaultMessage: 'No account with the client as holder was aggregated.',
    description: 'Error message when the analysis contains account with an unsupported ownership',
  },
  [AnalysesErrorMessages.NO_ANALYSES]: {
    id: 'dashboard.no_analyses',
    defaultMessage: "The user didn't aggregated a bank account.",
    description: 'Error message when no analyses exist',
  },
  [AnalysesErrorMessages.AGGREGATION_FAILED]: {
    id: 'dashboard.aggregation_failed',
    defaultMessage: 'The connection identifiers used were not valid.',
    description: 'Error message when the analysis contains no checking account',
  },
  [AnalysesErrorMessages.AUTHENTICATION_FAILED]: {
    id: 'dashboard.authentication_failed',
    defaultMessage: 'The authentication was not validated by the bank.',
    description: 'Error message when no analyses exist',
  },
  [AnalysesErrorMessages.LOW_ACTIVITY_SCORE]: {
    id: 'dashboard.low_activity_score',
    defaultMessage: 'Insufficient activity on accounts.',
    description: 'Error message when no analyses exist',
  },
  [AnalysesErrorMessages.ZERO_INCOME]: {
    id: 'dashboard.zero_income',
    defaultMessage: 'No regular income detected.',
    description: 'Error message when no analyses exist',
  },
  [StateFamily.VOID]: {
    id: 'dashboard.state_family.void',
    defaultMessage: 'The file has been created, but the aggregation process has not yet begun.',
    description: 'Messages when the user has created a customer but the aggregation process has not yet begun.',
  },
  [StateFamily.PENDING]: {
    id: 'dashboard.state_family.pending',
    defaultMessage: 'Invitation sent',
    description: 'Message when the invitation has been sent.',
  },
});

export const analysisStateMessages = defineMessages({
  title: {
    id: 'dashboard.analysis_state.title',
    defaultMessage: "Analysis' status",
    description: 'Title to the state of the analysis',
  },
  [StateFamily.FLOW_STARTED]: {
    id: 'dashboard.analysis_state.state_family.flow_started',
    defaultMessage: 'Flow started',
    description: 'Title for messages of error when Aggregation flow was interrupted.',
  },
  [StateFamily.AGGREGATION_STARTED]: {
    id: 'dashboard.analysis_state.state_family.aggregation_started',
    defaultMessage: 'Aggregation started',
    description: 'Title for messages of error when Aggregation started.',
  },
  [StateFamily.IN_ERROR]: {
    id: 'dashboard.analysis_state.state_family.in_error',
    defaultMessage: 'In error',
    description: 'Title for messages of error during the analysis.',
  },
  [StateFamily.VOID]: {
    id: 'dashboard.analysis_state.state_family.void',
    defaultMessage: 'New folder',
    description:
      'Title for messages when the user has created a customer but the aggregation process has not yet begun.',
  },
  [StateFamily.PENDING]: {
    id: 'dashboard.analysis_state.state_family.pending',
    defaultMessage: 'Invitation sent',
    description: 'Title for messages when the invitation has been sent.',
  },
  [StateFamily.HAS_ANALYSIS]: {
    id: 'dashboard.analysis_state.state_family.has_analysis',
    defaultMessage: 'Analysis available',
    description: 'Title for messages when the customer has analysis.',
  },
  [StateFamily.ANALYSIS_SUCCESS_PDF]: {
    id: 'dashboard.analysis_state.state_family.analysis_success_pdf',
    defaultMessage: 'Analysis available',
    description: 'Title for messages when the customer has analysis success pdf.',
  },
  [StateFamily.PENDING_PDF]: {
    id: 'dashboard.analysis_state.state_family.pending_pdf',
    defaultMessage: 'Reading PDF in progress',
    description: 'Title for messages when the customer has analysis pending pdf.',
  },
  [StateFamily.IN_ERROR_PDF]: {
    id: 'dashboard.analysis_state.state_family.in_error_pdf',
    defaultMessage: 'In error',
    description: 'Title for messages of error during the analysis.',
  },
  date: {
    id: 'dashboard.analysis_state.date',
    defaultMessage: 'Date',
    description: 'Title of the date of the analysis state',
  },
  details: {
    id: 'dashboard.analysis_state.details',
    defaultMessage: 'Details',
    description: 'Title of the details of the analysis state',
  },
});

export const analysisStateTooltipMessages = defineMessages({
  [StateFamily.FLOW_STARTED]: {
    id: 'dashboard.analysis_state.state_family.tooltip.flow_started',
    defaultMessage: 'Flow started',
    description: 'Title for messages of error when flow was started.',
  },
  [StateFamily.AGGREGATION_STARTED]: {
    id: 'dashboard.analysis_state.state_family.tooltip.aggregation_started',
    defaultMessage: 'Aggregation started',
    description: 'Title for messages of error when Aggregation started.',
  },
  [StateFamily.IN_ERROR]: {
    id: 'dashboard.analysis_state.state_family.tooltip.in_error',
    defaultMessage: 'In error',
    description: 'Title for messages of error during the analysis.',
  },
  [StateFamily.VOID]: {
    id: 'dashboard.analysis_state.state_family.tooltip.void',
    defaultMessage: 'Aggregation process not started',
    description:
      'Title for messages when the user has created a customer but the aggregation process has not yet begun.',
  },
  [StateFamily.PENDING]: {
    id: 'dashboard.analysis_state.state_family.tooltip.pending',
    defaultMessage: 'Invitation sent',
    description: 'Title for messages when the invitation has been sent.',
  },
  [StateFamily.HAS_ANALYSIS]: {
    id: 'dashboard.analysis_state.state_family.tooltip.has_analysis',
    defaultMessage: 'Analysis available',
    description: 'Title for messages when the customer has analysis.',
  },
  [StateFamily.ANALYSIS_SUCCESS_PDF]: {
    id: 'dashboard.analysis_state.state_family.tooltip.analysis_success_pdf',
    defaultMessage: 'Analysis available',
    description: 'Title for messages when the customer has analysis success pdf.',
  },
  [StateFamily.PENDING_PDF]: {
    id: 'dashboard.analysis_state.state_family.tooltip.pending_pdf',
    defaultMessage: 'Reading PDF in progress',
    description: 'Title for messages when the customer has analysis pending pdf.',
  },
  [StateFamily.IN_ERROR_PDF]: {
    id: 'dashboard.analysis_state.state_family.tooltip.in_error',
    defaultMessage: 'In error',
    description: 'Title for messages of error during the analysis.',
  },
});
